import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { submitLead } from "../lib/api";
import { ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";

const INIT = {
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    origin: "",
    destination: "",
    cargo_type: "",
    volume: "",
    message: "",
    interest: "quote",
};

export default function Quote() {
    const [form, setForm] = useState(INIT);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        try {
            setBusy(true);
            await submitLead(form);
            setDone(true);
            toast.success("Quote request received. We&apos;ll respond within one business day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="quote-page" className="bg-[#0A0A0E] text-zinc-100 min-h-screen">
            <Navbar />
            <main className="pt-28 pb-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 mb-6">
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="section-tag">Get a quote</span>
                        <h1 className="font-display text-4xl sm:text-5xl text-white mt-5 leading-tight tracking-tighter">
                            Tell us about your freight.
                        </h1>
                        <p className="text-zinc-400 mt-6 leading-relaxed">
                            We&apos;ll come back with options — diagnostic, advisory or platform —
                            with a transparent cost and the trade-offs of each.
                        </p>
                        <p className="text-xs font-mono-accent text-zinc-500 mt-8 uppercase tracking-[0.18em]">
                            One business day · no obligation
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="card-glow rounded-2xl p-7 lg:p-9">
                            {done ? (
                                <div className="text-center py-8" data-testid="quote-success">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-cyan-400/40 bg-cyan-400/10 mb-5">
                                        <CheckCircle2 size={28} className="text-cyan-300" />
                                    </div>
                                    <h3 className="font-display text-2xl text-white">Quote request received</h3>
                                    <p className="text-zinc-400 mt-2 max-w-md mx-auto">We&apos;ll be in touch within one business day with a tailored response.</p>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="quote-form">
                                    <Input id="q-name" label="Full name *" value={form.name} onChange={set("name")} required />
                                    <Input id="q-email" type="email" label="Work email *" value={form.email} onChange={set("email")} required />
                                    <Input id="q-company" label="Company" value={form.company} onChange={set("company")} />
                                    <Input id="q-role" label="Role" value={form.role} onChange={set("role")} />
                                    <Input id="q-phone" label="Phone" value={form.phone} onChange={set("phone")} />
                                    <Input id="q-cargo" label="Cargo type" value={form.cargo_type} onChange={set("cargo_type")} placeholder="e.g. electronics, FMCG, machinery" />
                                    <Input id="q-origin" label="Origin (country / port)" value={form.origin} onChange={set("origin")} placeholder="e.g. Shanghai, CN" />
                                    <Input id="q-destination" label="Destination" value={form.destination} onChange={set("destination")} placeholder="e.g. Sydney, AU" />
                                    <Input id="q-volume" label="Volume / frequency" value={form.volume} onChange={set("volume")} placeholder="e.g. 20 × 40HC per month" />
                                    <div className="sm:col-span-2">
                                        <span className="block text-xs font-mono-accent text-zinc-400 mb-2 uppercase tracking-[0.18em]">
                                            Anything else?
                                        </span>
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={set("message")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm resize-none"
                                            placeholder="Tell us what's hurting today — surcharges, visibility, tender timing..."
                                            data-testid="q-message"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="quote-submit">
                                            {busy ? "Sending..." : "Request quote"} <ArrowRight size={16} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Input({ id, label, type = "text", value, onChange, required, placeholder }) {
    return (
        <label className="block" htmlFor={id}>
            <span className="block text-xs font-mono-accent text-zinc-400 mb-2 uppercase tracking-[0.18em]">{label}</span>
            <input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                data-testid={`input-${id}`}
            />
        </label>
    );
}
